import { createReducer, createAction } from 'redux-act';
import { createSelector } from 'reselect';
import { selectCurrentId } from '@gandi/redux-sharing';
import { createResource } from '@gandi/resource/redux';
import { resource as infosResource, fetchInfos, selectIsLoaded, selectIsFetching, selectIsFailure, selectIsToRefetch } from './infos';
import { selectRaw as selectRawState, selectSharingIdProp } from './utils';
export { selectIsLoaded, selectIsFetching, selectIsFailure, selectIsToRefetch };
export const fetchPrepaid = fetchInfos;
export const setWarningThreshold = createAction('[billing.prepaid] Set Warning Threshold');
export const setAutomaticCredit = createAction('[billing.prepaid] Set Automatic Credit');
export const setOptimisticPrepaid = createAction('[billing.prepaid] Set Optimistic Prepaid');
export const selectRaw = createSelector([selectRawState], raw => raw.prepaid);
const getPrepaid = (raw, sharingId) => raw[sharingId];
export const selectPrepaid = createSelector([selectRaw, selectSharingIdProp], getPrepaid);
export const selectPrepaidForCurrent = createSelector([selectRaw, selectCurrentId], getPrepaid);
const getPrepaidId = (prepaid = {}) => prepaid.id;
export const selectPrepaidId = createSelector([selectPrepaid, selectSharingIdProp], getPrepaidId);
export const selectPrepaidIdForCurrent = createSelector([selectPrepaidForCurrent], getPrepaidId);
const getPrepaidIsOptimistic = prepaid => prepaid ? !prepaid.id : false;
export const selectPrepaidIsOptimistic = createSelector([selectPrepaid, selectSharingIdProp], getPrepaidIsOptimistic);
export const selectPrepaidIsOptimisticForCurrent = createSelector([selectPrepaidForCurrent], getPrepaidIsOptimistic);
const getCurrency = (prepaid = {}) => prepaid.currency;
export const selectCurrency = createSelector([selectPrepaid], getCurrency);
export const selectCurrencyForCurrent = createSelector([selectPrepaidForCurrent], getCurrency);
const getWarningThreshold = (prepaid = {}) => prepaid.warningThreshold;
export const selectWarningThreshold = createSelector([selectPrepaid], getWarningThreshold);
export const selectWarningThresholdForCurrent = createSelector([selectPrepaidForCurrent], getWarningThreshold);
const getAutomaticCredit = (prepaid = {}) => prepaid.automaticCredit;
export const selectAutomaticCredit = createSelector([selectPrepaid], getAutomaticCredit);
export const selectAutomaticCreditForCurrent = createSelector([selectPrepaidForCurrent], getAutomaticCredit);
const getPrepaidAmount = (prepaid = {}) => prepaid.amount;
export const selectPrepaidAmount = createSelector([selectPrepaid], getPrepaidAmount);
export const selectPrepaidAmountForCurrent = createSelector([selectPrepaidForCurrent], getPrepaidAmount);
const selectIsLoadedForCurrent = state => {
  const sharingId = selectCurrentId(state);
  return selectIsLoaded(state, {
    sharingId
  });
};
const getCanChangeCurrency = (isLoaded, prepaid) => isLoaded ? !prepaid || prepaid.amount === undefined || prepaid.amount === null || prepaid.amount === 0 : undefined;
export const selectCanChangeCurrency = createSelector([selectIsLoaded, selectPrepaid], getCanChangeCurrency);
export const selectCanChangeCurrencyForCurrent = createSelector([selectIsLoadedForCurrent, selectPrepaidForCurrent], getCanChangeCurrency);
const formatPrepaidAPIReturn = ({
  prepaid
}) => {
  if (prepaid) {
    return {
      warningThreshold: prepaid.warning_threshold || null,
      dateCreated: prepaid.date_created,
      dateUpdated: prepaid.date_updated,
      currency: prepaid.currency,
      amount: Number.parseFloat(prepaid.amount),
      id: prepaid.id,
      ...(prepaid.automatic_credit && {
        automaticCredit: prepaid.automatic_credit
      })
    };
  }
  return undefined;
};
export const selectIsPrepaidInUse = (state, {
  sharingId
}) => !!state.gandiReduxBilling.prepaid[sharingId] && (state.gandiReduxBilling.prepaid[sharingId].dateCreated !== state.gandiReduxBilling.prepaid[sharingId].dateUpdated || state.gandiReduxBilling.prepaid[sharingId].amount !== 0);
export const resource = createResource('billing.billing.prepaid');
export const selectIsFetchingLast = (state, {
  sharingId
}) => resource.selectIsFetchingOne(state, sharingId);
export const selectIsLoadedLast = (state, {
  sharingId
}) => resource.selectIsLoadedOne(state, sharingId);
export const selectIsFailureLast = (state, {
  sharingId
}) => resource.selectIsFailureOne(state, sharingId);
export const selectIsToRefetchLast = (state, {
  sharingId
}) => resource.selectIsToRefetchOne(state, sharingId);
export const selectIsCreating = (state, {
  sharingId
}) => resource.selectIsCreatingOne(state, sharingId);
export const selectIsPatching = (state, {
  sharingId
}) => resource.selectIsPatchingOne(state, sharingId);
export const selectIsDeleting = (state, {
  sharingId
}) => resource.selectIsDeletingOne(state, sharingId);
export const fetchLastPrepaid = (sharingId, options = {
  sharingId: undefined,
  params: undefined
}) => dispatch => dispatch(resource.fetchOne('/billing/prepaids', {
  ...options,
  sharingId,
  label: sharingId,
  params: {
    sharing_id: sharingId,
    per_page: 1,
    ...(options.params || {})
  },
  on200: response => response,
  not200: response => Promise.reject(response)
}));
export const patchPrepaid = (params = {}, options = {}) => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  const floatifyParam = param => {
    if (typeof param === 'object' && param !== null) {
      return Object.fromEntries(Object.entries(param).map(([k, v]) => [k, floatifyParam(v)]));
    }
    return Number.isNaN(parseFloat(param)) ? null : parseFloat(param);
  };
  const warningThreshold = params.warningThreshold !== undefined ? floatifyParam(params.warningThreshold) : undefined;
  const automaticCredit = params.automaticCredit !== undefined ? floatifyParam(params.automaticCredit) : undefined;
  const optimistic = params.optimistic || false;
  const body = (() => {
    if (warningThreshold !== undefined) {
      return {
        warning_threshold: warningThreshold
      };
    }
    if (automaticCredit !== undefined) {
      return {
        automatic_credit: automaticCredit
      };
    }
    return {};
  })();
  if (!sharingId) {
    return Promise.reject(new Error('[billing.prepaid] `patchPrepaid` called without `sharingId` param' + ' nor `prepaid` object in the state.'));
  }
  const previousWarningThreshold = selectWarningThreshold(getState(), {
    sharingId
  });
  const previousAutomaticCredit = selectAutomaticCredit(getState(), {
    sharingId
  });
  if (optimistic && warningThreshold !== undefined) {
    dispatch(setWarningThreshold({
      warningThreshold,
      sharingId
    }));
  }
  if (optimistic && automaticCredit !== undefined) {
    const automaticCreditObject = automaticCredit === null ? {
      mycard_id: previousAutomaticCredit?.mycard_id
    } : {
      ...automaticCredit,
      mycard_id: previousAutomaticCredit?.mycard_id
    };
    dispatch(setAutomaticCredit({
      automaticCredit: automaticCreditObject,
      sharingId
    }));
  }
  return dispatch(resource.patchOne(`/billing/prepaids/${sharingId}`, body, {
    ...options,
    params: {
      sharing_id: sharingId,
      ...(options.params || {})
    },
    label: sharingId,
    on200: response => response,
    not200: response => Promise.reject(response)
  })).catch(err => {
    if (optimistic && warningThreshold !== undefined) {
      dispatch(setWarningThreshold({
        warningThreshold: previousWarningThreshold,
        sharingId
      }));
      if (optimistic && automaticCredit !== undefined) {
        dispatch(setAutomaticCredit({
          automaticCredit: previousAutomaticCredit,
          sharingId
        }));
      }
    }
    throw err;
  });
};
export const createPrepaid = (params, options = {}) => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.prepaid] `createPrepaid` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  const {
    currency
  } = params;
  const optimistic = params.optimistic || false;
  const body = {
    currency
  };
  return dispatch(resource.createOne('/billing/prepaids', body, {
    ...options,
    params: {
      ...options.params,
      sharing_id: sharingId
    },
    label: sharingId,
    on201: response => {
      if (optimistic) dispatch(setOptimisticPrepaid({
        ...body,
        sharingId
      }));
      return response;
    },
    not201: response => Promise.reject(response)
  }));
};
export const deletePrepaid = (options = {}) => (dispatch, getState) => {
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.prepaid] `deletePrepaid` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  return dispatch(resource.deleteOne(`/billing/prepaids/${sharingId}`, {
    ...options,
    params: {
      ...options.params,
      sharing_id: sharingId
    },
    label: sharingId,
    on200: response => response,
    not200: response => Promise.reject(response)
  }));
};
export const replacePrepaid = (params = {}, options = {
  sharingId: undefined,
  params: undefined
}) => (dispatch, getState) => {
  const {
    currency
  } = params;
  const currentId = selectCurrentId(getState());
  const sharingId = options.sharingId || currentId;
  if (typeof sharingId !== 'string') {
    return Promise.reject(new Error('[billing.prepaid] `replacePrepaid` called without `sharingId` option' + ' nor @gandi/redux-sharing dep/state.'));
  }
  const prepaid = selectPrepaid(getState(), {
    sharingId
  });
  let promise = Promise.resolve();
  if (prepaid) {
    promise = dispatch(deletePrepaid(options));
  }
  return promise.then(() => dispatch(createPrepaid({
    currency
  }, options))).then(() => dispatch(fetchPrepaid(options)));
};
const reducer = createReducer({
  [resource.fetchOne.success.getType()]: (state, {
    response: {
      data
    },
    label: sharingId
  }) => {
    if (Array.isArray(data) && data[0]) {
      return {
        ...state,
        [sharingId]: formatPrepaidAPIReturn({
          prepaid: data[0]
        })
      };
    }
    return state;
  },
  [infosResource.fetchOne.success.getType()]: (state, {
    response: {
      data
    },
    label: sharingId
  }) => ({
    ...state,
    [sharingId]: state[sharingId] ? {
      ...(state[sharingId] || {}),
      ...formatPrepaidAPIReturn(data)
    } : formatPrepaidAPIReturn(data)
  }),
  [setOptimisticPrepaid]: (state, {
    currency,
    sharingId
  }) => ({
    ...state,
    [sharingId]: {
      currency,
      amount: 0
    }
  }),
  [setWarningThreshold]: (state, {
    warningThreshold,
    sharingId
  }) => ({
    ...state,
    [sharingId]: {
      ...(state[sharingId] || {}),
      warningThreshold
    }
  }),
  [setAutomaticCredit]: (state, {
    automaticCredit,
    sharingId
  }) => ({
    ...state,
    [sharingId]: {
      ...(state[sharingId] || {}),
      automaticCredit
    }
  })
}, {});
export default reducer;